import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Newspaper, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { content } from "@/i18n/content";
import { EASE, Reveal, SectionHeading } from "@/components/Reveal";
import { avifSrc, isLocalPhoto, webpSrc } from "@/lib/media";

const TILTS = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "rotate-1", "-rotate-2", "rotate-2"];

const InTheNews = () => {
  const { pick } = useLanguage();
  const [active, setActive] = useState(null);
  const items = content.news.items;

  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) => setActive((a) => (a === null ? null : (a + dir + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, step]);

  return (
    <section id="news" data-testid="news-section" className="bg-[#171717] py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-24">
        <SectionHeading
          dark
          eyebrow={pick(content.news.eyebrow)}
          heading={pick(content.news.heading)}
          sub={pick(content.news.sub)}
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {items.map((item, i) => (
            <Reveal key={item.src} delay={(i % 4) * 0.08} y={36}>
              <button
                type="button"
                data-testid={`news-item-${i}`}
                onClick={() => setActive(i)}
                className={`group relative block w-full bg-[#F7F5F0] p-2.5 pb-8 shadow-[0_18px_40px_-16px_rgba(0,0,0,0.6)] transition-transform duration-500 hover:-translate-y-1.5 hover:rotate-0 ${TILTS[i % TILTS.length]}`}
                aria-label={pick(item.caption)}
              >
                <span className="relative block aspect-[3/4] w-full overflow-hidden bg-[#e8e5dd]">
                  <picture>
                    {isLocalPhoto(item.src) && (
                      <>
                        <source srcSet={avifSrc(item.src)} type="image/avif" />
                        <source srcSet={webpSrc(item.src)} type="image/webp" />
                      </>
                    )}
                    <img
                      src={item.src}
                      alt={pick(item.caption)}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                    />
                  </picture>
                  <span className="absolute inset-0 bg-black/0 transition-colors duration-500 group-hover:bg-black/10" aria-hidden="true" />
                </span>
                <span className="mt-3 flex items-center gap-2 px-0.5 text-left text-[11px] font-medium leading-snug text-[#171717]/70">
                  <Newspaper size={13} className="shrink-0 text-[#C42021]" aria-hidden="true" />
                  {pick(item.caption)}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="news-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={pick(items[active].caption)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0c0c0c]/95 backdrop-blur-md p-4 md:p-12"
            onClick={close}
          >
            <button
              type="button"
              data-testid="news-lightbox-close-btn"
              onClick={close}
              aria-label={pick(content.news.closeLabel)}
              className="absolute top-5 right-5 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] transition-colors hover:bg-[#C42021] hover:border-[#C42021]"
            >
              <X size={20} />
            </button>
            <button
              type="button"
              data-testid="news-lightbox-prev-btn"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous"
              className="absolute left-3 md:left-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] transition-colors hover:bg-[#F7F5F0] hover:text-[#171717]"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              data-testid="news-lightbox-next-btn"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next"
              className="absolute right-3 md:right-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] transition-colors hover:bg-[#F7F5F0] hover:text-[#171717]"
            >
              <ChevronRight size={20} />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <picture>
                {isLocalPhoto(items[active].src) && (
                  <>
                    <source srcSet={avifSrc(items[active].src)} type="image/avif" />
                    <source srcSet={webpSrc(items[active].src)} type="image/webp" />
                  </>
                )}
                <img
                  src={items[active].src}
                  alt={pick(items[active].caption)}
                  className="max-h-[78vh] w-auto max-w-full object-contain"
                />
              </picture>
              <figcaption className="mt-4 text-center text-sm tracking-wide text-[#F7F5F0]/80">
                {pick(items[active].caption)}
                <span className="ml-3 text-[#F7F5F0]/40">
                  {active + 1} / {items.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default InTheNews;
